import { useState } from 'react';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  TableCellsIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
  LinkIcon,
  GlobeAltIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CREDIT_COST = 10;
const COLUMN_TYPES = ['STRING', 'INTEGER', 'FLOAT', 'BOOLEAN', 'DATE'];

export default function AIResearchSource({ companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1);

  // Step 1: Prompt + schema
  const [prompt, setPrompt] = useState('');
  const [columns, setColumns] = useState([{ name: '', type: 'STRING' }]);
  const [domainFilters, setDomainFilters] = useState([]);
  const [domainInput, setDomainInput] = useState('');

  // Preview
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Import
  const [name, setName] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const addColumn = () => {
    setColumns([...columns, { name: '', type: 'STRING' }]);
  };

  const removeColumn = (index) => {
    if (columns.length <= 1) return;
    setColumns(columns.filter((_, i) => i !== index));
  };

  const updateColumn = (index, field, value) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], [field]: value };
    setColumns(updated);
  };

  const validColumns = columns.filter(c => c.name.trim());

  const addDomain = () => {
    const domain = domainInput.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (domain && !domainFilters.includes(domain)) {
      setDomainFilters([...domainFilters, domain]);
    }
    setDomainInput('');
  };

  const removeDomain = (index) => {
    setDomainFilters(domainFilters.filter((_, i) => i !== index));
  };

  const handlePreview = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a research prompt');
      return;
    }
    if (validColumns.length === 0) {
      toast.error('Please define at least one column with a name');
      return;
    }

    setIsPreviewing(true);
    setPreview(null);
    setPreviewError(null);

    try {
      const res = await apiHelpers.previewAiResearch({
        prompt,
        columns: validColumns,
        domainFilters,
        companyId
      });

      if (res.data.success) {
        setPreview(res.data);
        setPreviewError(null);
        setStep(2);
        if (!name) {
          setName('AI Research Data');
        }
      } else {
        setPreviewError({
          errorType: res.data.errorType,
          message: res.data.message,
          citations: res.data.citations || []
        });
        setStep(2);
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to generate research data';
      if (error.response?.status === 402) {
        toast.error(`Insufficient credits. You need ${CREDIT_COST} credits.`);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for this data source');
      return;
    }

    setIsImporting(true);

    try {
      const res = await apiHelpers.generateAiResearch({
        name,
        prompt,
        columns: preview.columns,
        previewData: preview.data,
        citations: preview.citations || [],
        domainFilters,
        companyId
      });

      if (res.data.success) {
        toast.success('Data imported successfully!');
        onSuccess(res.data.dataSource);
      } else {
        toast.error(res.data.error || 'Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import data');
    } finally {
      setIsImporting(false);
    }
  };

  const handleTryAgain = () => {
    setPreview(null);
    setPreviewError(null);
    setStep(1);
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { num: 1, label: 'Prompt & Schema' },
          { num: 2, label: 'Preview' },
          { num: 3, label: 'Import' }
        ].map(({ num, label }, idx) => (
          <div key={num} className="flex items-center gap-2">
            {idx > 0 && <ChevronRightIcon className="w-4 h-4 text-surface-400" />}
            <div className={`flex items-center gap-1.5 ${
              step === num
                ? 'text-primary-600 dark:text-primary-400 font-medium'
                : step > num
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-surface-400'
            }`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step === num
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : step > num
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                    : 'bg-surface-100 dark:bg-surface-800'
              }`}>
                {step > num ? <CheckCircleIcon className="w-4 h-4" /> : num}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Step 1: Research Prompt + Schema Builder */}
      {step === 1 && (
        <div className="space-y-6">
          {/* How it works banner */}
          <div className="card p-4 bg-surface-50 dark:bg-surface-800/50 border-surface-200 dark:border-surface-700">
            <h4 className="text-sm font-medium text-surface-900 dark:text-white mb-2">How AI Research works</h4>
            <ul className="space-y-1.5 text-xs text-surface-600 dark:text-surface-400">
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-0.5">1.</span>
                <span>You describe the data you need and define the columns for your dataset.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-0.5">2.</span>
                <span>Our AI searches the live web in real time and compiles the results into a structured table.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-0.5">3.</span>
                <span>You preview the data, then import it into your data warehouse for charting and analysis.</span>
              </li>
            </ul>
            <div className="mt-3 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                <strong>Important:</strong> Because the AI searches the live web each time, results may vary between generations. The same prompt can return different rows, different values, or a different number of results. This is best suited for market research, benchmarks, and trend analysis rather than exact catalog data.
              </p>
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Research Prompt
              </label>
              <div className="relative">
                <SparklesIcon className="absolute left-3 top-3 w-5 h-5 text-surface-400" />
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder='e.g. "Find the top 10 largest banks in the US by total assets, including their year-over-year growth rates for 2024-2025."'
                  rows={4}
                  maxLength={2000}
                  className="input pl-10 w-full"
                />
              </div>
              <p className="mt-1 text-xs text-surface-500 flex justify-between">
                <span>Describe the data you want the AI to research from the live web.</span>
                <span>{prompt.length}/2000</span>
              </p>
            </div>

            {/* Schema Builder */}
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Define Your Columns
              </label>
              <p className="text-xs text-surface-500 mb-3">
                Specify the exact columns you need in your dataset. The AI will fill each column with researched data.
              </p>

              <div className="space-y-2">
                {columns.map((col, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={col.name}
                      onChange={(e) => updateColumn(index, 'name', e.target.value)}
                      placeholder={`Column ${index + 1} name (e.g. "Company Name")`}
                      className="input flex-1"
                      maxLength={100}
                    />
                    <select
                      value={col.type}
                      onChange={(e) => updateColumn(index, 'type', e.target.value)}
                      className="input w-32"
                    >
                      {COLUMN_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeColumn(index)}
                      disabled={columns.length <= 1}
                      className="p-2 text-surface-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addColumn}
                className="mt-2 flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700"
              >
                <PlusIcon className="w-4 h-4" />
                Add Column
              </button>

              {/* Auto-included columns */}
              <div className="mt-4 p-3 bg-surface-50 dark:bg-surface-800/50 rounded-lg">
                <p className="text-xs font-medium text-surface-500 mb-2">Auto-included columns:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-200/50 dark:bg-surface-700/50 rounded text-xs text-surface-600 dark:text-surface-400">
                    <LinkIcon className="w-3 h-3" />
                    Source_URL
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-200/50 dark:bg-surface-700/50 rounded text-xs text-surface-600 dark:text-surface-400">
                    <TableCellsIcon className="w-3 h-3" />
                    Generated_On
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Optional: Focus Sources */}
          <div className="card p-6 space-y-3">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
              Focus Sources
              <span className="text-xs font-normal text-surface-500 ml-1">(optional)</span>
            </label>
            <p className="text-xs text-surface-500">
              Restrict the AI&apos;s search to specific websites. Leave empty to search the entire web.
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addDomain(); }
                  }}
                  placeholder="e.g. tokopedia.com"
                  className="input pl-9 w-full"
                />
              </div>
              <button
                type="button"
                onClick={addDomain}
                disabled={!domainInput.trim()}
                className="btn btn-secondary flex items-center gap-1 text-sm"
              >
                <PlusIcon className="w-4 h-4" />
                Add
              </button>
            </div>
            {domainFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {domainFilters.map((domain, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-full text-xs text-primary-700 dark:text-primary-300"
                  >
                    <GlobeAltIcon className="w-3 h-3" />
                    {domain}
                    <button
                      type="button"
                      onClick={() => removeDomain(i)}
                      className="ml-0.5 hover:text-red-500 transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Credit cost callout */}
          <div className="card p-4 bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800">
            <div className="flex items-center gap-3">
              <SparklesIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-cyan-900 dark:text-cyan-100">
                  This will cost {CREDIT_COST} credits
                </p>
                <p className="text-xs text-cyan-700 dark:text-cyan-300">
                  Credits are only charged if the AI successfully returns data. If no data is found, you are not charged.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-sm text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handlePreview}
              disabled={isPreviewing || !prompt.trim() || validColumns.length === 0}
              className="btn btn-primary flex items-center gap-2"
            >
              {isPreviewing ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Researching...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4" />
                  Generate Preview
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview or Error */}
      {step === 2 && previewError && (
        <div className="space-y-6">
          {/* Error state */}
          <div className="card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
              No Data Found
            </h3>
            <p className="text-surface-600 dark:text-surface-400 mb-4 max-w-md mx-auto">
              {previewError.message || 'We searched the live web but couldn\'t compile a reliable dataset for this request.'}
            </p>
            <div className="bg-surface-50 dark:bg-surface-800/50 rounded-lg p-3 mb-4 max-w-md mx-auto">
              <p className="text-xs text-surface-500">
                <strong>Tip:</strong> Try broadening your search terms or asking for more common industry metrics.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 mb-6">
              <CheckCircleIcon className="w-4 h-4" />
              0 Credits Charged
            </span>
            <div className="flex justify-center gap-3">
              <button
                onClick={handleTryAgain}
                className="btn btn-primary flex items-center gap-2"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={onBack}
                className="btn bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && preview && !previewError && (
        <div className="space-y-6">
          {/* Success preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TableCellsIcon className="w-5 h-5 text-emerald-500" />
                <h3 className="font-medium text-surface-900 dark:text-white">
                  Preview ({preview.rowCount} rows)
                </h3>
              </div>
              <span className="text-xs text-surface-500">
                {CREDIT_COST} credits charged
              </span>
            </div>

            {/* Data table */}
            <div className="overflow-x-auto border border-surface-200 dark:border-surface-700 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-50 dark:bg-surface-800">
                    {preview.columns.map((col, i) => (
                      <th key={i} className="px-3 py-2 text-left text-xs font-medium text-surface-500 uppercase tracking-wider whitespace-nowrap">
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                  {preview.data.slice(0, 10).map((row, rowIdx) => (
                    <tr key={rowIdx} className="hover:bg-surface-50 dark:hover:bg-surface-800/50">
                      {preview.columns.map((col, colIdx) => (
                        <td key={colIdx} className="px-3 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate">
                          {col.name === 'Source_URL' && row[col.name] ? (
                            <a
                              href={row[col.name]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {row[col.name].length > 50 ? row[col.name].substring(0, 50) + '...' : row[col.name]}
                            </a>
                          ) : (
                            String(row[col.name] ?? '—')
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.data.length > 10 && (
              <p className="mt-2 text-xs text-surface-500 text-center">
                Showing 10 of {preview.rowCount} rows
              </p>
            )}
          </div>

          {/* Citations */}
          {preview.citations && preview.citations.length > 0 && (
            <div className="card p-4">
              <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2 flex items-center gap-1.5">
                <LinkIcon className="w-4 h-4" />
                Sources ({preview.citations.length})
              </h4>
              <div className="space-y-1">
                {preview.citations.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-primary-600 dark:text-primary-400 hover:underline truncate"
                  >
                    {url}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Top US Banks by Assets"
              className="input w-full"
              maxLength={100}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleTryAgain}
              className="flex items-center gap-1 text-sm text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Start Over
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || !name.trim()}
              className="btn btn-primary flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  Import Data
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Import Complete */}
      {step === 3 && (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
            Import Complete!
          </h3>
          <p className="text-surface-600 dark:text-surface-400">
            Your AI-generated research data has been imported and is ready for charting.
          </p>
        </div>
      )}
    </div>
  );
}
